import { ApolloError } from '@apollo/client';
import { createSelector } from 'reselect';

import { RootState, ThunkAction } from '@zoopla/redux-types';
import {
    ACTION_TYPES_USER,
    UserState,
    UserActionTypes,
    LoggedInStatus
} from '@zoopla/redux-types/user.types';

import { getApolloClient } from '@zoopla/apollo';
import { User, USER_QUERY, FetchUserQuery } from '@zoopla/user-queries';

// Actions
export const getUserAttempt = () => ({
    type: ACTION_TYPES_USER.GET_USER_ATTEMPT
});

export const getUserSuccess = (user: FetchUserQuery['user']) => ({
    type: ACTION_TYPES_USER.GET_USER_SUCCESS,
    user
});

export const getUserFailure = (error: Error) => ({
    type: ACTION_TYPES_USER.GET_USER_FAILURE,
    error
});

export const setLoggedIn = (isLoggedIn: boolean) => ({
    type: ACTION_TYPES_USER.SET_LOGGED_IN,
    isLoggedIn
});

export const setUser = (user: User) => ({
    type: ACTION_TYPES_USER.SET_USER,
    user
});

// Reducer
export const initialState: UserState = {
    loading: false,
    loggedIn: false,
    loggedInStatus: 'initial',
    registered: false,
    senderPropertyStatus: null,
    creationDate: null,
    userId: null,
    userUuid: null,
    error: []
};

export const user = (
    state: UserState = initialState,
    action: UserActionTypes
): UserState => {
    switch (action.type) {
        case ACTION_TYPES_USER.GET_USER_ATTEMPT:
            return {
                ...state,
                loading: true,
                loggedInStatus: 'loading'
            };
        case ACTION_TYPES_USER.GET_USER_SUCCESS:
            return {
                ...state,
                loading: false,
                loggedIn: action.user?.loggedIn,
                loggedInStatus: action.user?.loggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn',
                name: action.user?.name,
                email: action.user?.email,
                senderPropertyStatus: action.user?.senderPropertyStatus,
                creationDate: action.user?.creationDate,
                userId: action.user?.userId,
                userUuid: action.user?.userUuid,
                registered: action.user?.registered,
                registrationSources: action.user?.registrationSources
            };
        case ACTION_TYPES_USER.GET_USER_FAILURE:
            return {
                ...state,
                loading: false,
                loggedInStatus: 'error',
                error: [action.error]
            };
        case ACTION_TYPES_USER.SET_LOGGED_IN:
            return {
                ...state,
                loggedIn: action.isLoggedIn,
                loggedInStatus: action.isLoggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn'
            };
        case ACTION_TYPES_USER.SET_USER:
            return {
                ...state,
                loggedIn: action.user?.loggedIn,
                loggedInStatus: action.user?.loggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn',
                name: action.user?.name,
                email: action.user?.email,
                senderPropertyStatus: action.user?.senderPropertyStatus,
                creationDate: action.user?.creationDate,
                userId: action.user?.userId,
                userUuid: action.user?.userUuid,
                registered: action.user?.registered,
                registrationSources: action.user?.registrationSources
            };
        default:
            return state;
    }
};

// Action creators
export const fetchUser = (): ThunkAction => async (dispatch) => {
    dispatch(getUserAttempt());

    try {
        const {
            data: { user }
        } = await getApolloClient().query<FetchUserQuery>({
            query: USER_QUERY
        });

        dispatch(getUserSuccess(user));
    } catch (error) {
        dispatch(getUserFailure(error as ApolloError));
    }
};

// Selectors
export const getUserSelector = (state: RootState): UserState => state.user;
export const isLoggedInSelector = createSelector<RootState, UserState, boolean>(
    getUserSelector,
    (user) => !!user.loggedIn
);
export const loggedInStatusSelector = createSelector<
    RootState,
    UserState,
    LoggedInStatus
>(getUserSelector, (user) => user.loggedInStatus);
